import { useEffect, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Row, Col } from "react-bootstrap";
import { toast } from "react-toastify";
import Select from "react-select";

import "./EditParcelAccount.scss";

import { ReactComponent as FedexLogo } from "assets/images/iconsV2/fedex-seeklogo.svg";
import { ReactComponent as AlertIcon } from "assets/images/iconsV2/alert-circle-grey.svg";
import Badge from "components/v2/Badge/Badge";
import Switch from "components/v2/Switch/Switch";
import Radio from "components/v2/Radio/Radio";
import DualListField from "components/v2/DualList/DualListField";
import useSortedPaymentLocations from "hooks/useSortedPaymentLocations";
import Input from "components/v2/Input/Input";
import { useGetParcelAccountDetails } from "api/queries/user/useGetParcelAccountDetails";
import { useUpdateParcelAccount } from "api/queries/user/useUpdateParcelAccount";
import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import EmptyState from "components/v2/EmptyState/EmptyState";
import Button from "components/v2/Button/Button";
import useUnsavedChangesWarning from "hooks/useUnsavedChangesWarning";
import errorToast from "helpers/errorToast";
import { getLabelWithTranslationFallback } from "helpers/getLabel";

type ParcelAccountDetails = {
  id: number;
  is_active: boolean;
  account_name: string;
  account_number: string;
  account_type: string;
  label_format: "pdf" | "zpl";
  available_to_locations?: Array<{
    cb_account_number: string;
    location_name: string;
  }>;
  inv_reference_field?: string | null;
  ref_reference_field?: string | null;
  po_reference_field?: string | null;
  dept_reference_field?: string | null;
};

function EditParcelAccount() {
  const intl = useIntl();
  const { id } = useParams();
  const navigate = useNavigate();
  const methods = useForm({
    mode: "onChange",
    defaultValues: {
      is_active: false,
      account_name: "",
      account_number: "",
      account_type: "company",
      label_format: "pdf",
      available_to_locations: [],
      inv_reference_field: null,
      ref_reference_field: null,
      po_reference_field: null,
      dept_reference_field: null,
    },
  });
  const {
    control,
    reset,
    watch,
    formState: { isDirty, isValid },
  } = methods;
  const paymentLocations: any = useSortedPaymentLocations();
  const lastResetIdRef = useRef<number | null>(null);

  const { data: parcelAccountDetailsRaw, isInitialLoading: isParcelAccountDetailsFetching } = useGetParcelAccountDetails(
    +id!,
    {
      enabled: !!id,
    },
  );
  const parcelAccountDetails = parcelAccountDetailsRaw as ParcelAccountDetails | undefined;

  const { mutate: updateAccount, isLoading: isUpdating } = useUpdateParcelAccount();

  const { UnsavedChangesModal, confirmNavigation } = useUnsavedChangesWarning({
    isDirty,
    title: intl.formatMessage({
      id: "settings.parcelAccounts.editAccount.unsavedTitle",
      defaultMessage: "Discard account changes?",
    }),
    message: intl.formatMessage({
      id: "settings.parcelAccounts.editAccount.unsavedMessage",
      defaultMessage: "All of your changes will be lost. Proceed?",
    }),
  });

  useEffect(() => {
    if (!parcelAccountDetails || lastResetIdRef.current === parcelAccountDetails?.id) {
      return;
    }
    const formValues = {
      is_active: parcelAccountDetails.is_active ?? false,
      account_name: parcelAccountDetails.account_name ?? "",
      account_number: parcelAccountDetails.account_number ?? "",
      account_type: parcelAccountDetails.account_type ?? "company",
      label_format: parcelAccountDetails.label_format ?? "pdf",
      available_to_locations:
        parcelAccountDetails.available_to_locations?.map((location: any) => location.cb_account_number) ?? [],
      inv_reference_field: parcelAccountDetails.inv_reference_field
        ? String(parcelAccountDetails.inv_reference_field)
        : null,
      ref_reference_field: parcelAccountDetails.ref_reference_field
        ? String(parcelAccountDetails.ref_reference_field)
        : null,
      po_reference_field: parcelAccountDetails.po_reference_field ? String(parcelAccountDetails.po_reference_field) : null,
      dept_reference_field: parcelAccountDetails.dept_reference_field
        ? String(parcelAccountDetails.dept_reference_field)
        : null,
    };
    reset(formValues as any);
    lastResetIdRef.current = parcelAccountDetails.id;
  }, [parcelAccountDetails, reset]);

  const isActive = watch("is_active");
  const accountType = watch("account_type");

  const referenceOptions = [
    {
      id: 1,
      label: intl.formatMessage({
        id: "settings.parcelAccounts.createAccount.wbsField",
        defaultMessage: "WBS",
      }),
      value: "wbs",
    },
    {
      id: 2,
      label: intl.formatMessage({
        id: "settings.parcelAccounts.createAccount.shipmentId",
        defaultMessage: "Shipment ID",
      }),
      value: "shipment_id",
    },
    {
      id: 3,
      label: intl.formatMessage({
        id: "settings.parcelAccounts.createAccount.quickRequestCreator",
        defaultMessage: "Quick Request Creator",
      }),
      value: "quick_request_creator",
    },
    {
      id: 4,
      label: intl.formatMessage({
        id: "settings.parcelAccounts.createAccount.quoteRequestCreator",
        defaultMessage: "Quote Request Creator",
      }),
      value: "quote_request_creator",
    },
    {
      id: 5,
      label: intl.formatMessage({
        id: "settings.parcelAccounts.createAccount.pickupContactName",
        defaultMessage: "Pickup Contact Name",
      }),
      value: "pickup_contact_name",
    },
    {
      id: 6,
      label: intl.formatMessage({
        id: "settings.parcelAccounts.createAccount.deliveryContactName",
        defaultMessage: "Delivery Contact Name",
      }),
      value: "delivery_contact_name",
    },
    {
      id: 7,
      label: getLabelWithTranslationFallback("Custom field 01", {
        style: "capitalize",
        returnAsString: true,
        translation: (
          <FormattedMessage id="new-request.quote-confirmation.organisation.customField1" defaultMessage="Custom field 1" />
        ),
      }),
      value: "custom_field_01",
    },
  ];

  const onSubmit = (data: any) => {
    const payload: any = {
      is_active: data.is_active,
      account_name: data.account_name,
      account_type: data.account_type,
      label_format: data.label_format,
      available_to_locations: data.account_type === "general" ? data.available_to_locations : [],
      inv_reference_field: data.inv_reference_field || null,
      ref_reference_field: data.ref_reference_field || null,
      po_reference_field: data.po_reference_field || null,
      dept_reference_field: data.dept_reference_field || null,
    };

    updateAccount(
      { id: +id!, payload },
      {
        onSuccess: () => {
          toast.success(
            intl.formatMessage({
              id: "settings.parcelAccounts.editSuccess",
              defaultMessage: "Account edited successfully",
            }),
          );
          navigate("/settings/parcel-accounts");
        },
        onError: (error: any) => {
          errorToast(error);
        },
      },
    );
  };

  const handleDiscard = () => {
    confirmNavigation(() => {
      navigate("/settings/parcel-accounts", { replace: true });
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <div className="edit-parcel-account">
          <div className="edit-parcel-account__header">
            {intl.formatMessage({
              id: "settings.parcelAccounts.editAccount.title",
              defaultMessage: "View account",
            })}
          </div>
          <div className="text-md text-tertiary mb-3xl">
            {intl.formatMessage({
              id: "settings.parcelAccounts.editAccount.subtitle",
              defaultMessage: "Manage your parcel account's details here",
            })}
          </div>
          <div className="edit-parcel-account__body">
            {!parcelAccountDetails && isParcelAccountDetailsFetching && <LoadingSkeleton rows={6} />}
            {!parcelAccountDetails && !isParcelAccountDetailsFetching && (
              <EmptyState
                noActions
                customIcon={
                  <div className="">
                    <AlertIcon />
                  </div>
                }
                title={intl.formatMessage({
                  id: "general.couldNotLoadDetails",
                  defaultMessage: "We couldn't load these details",
                })}
                description={intl.formatMessage({
                  id: "general.tryAgainOrCheckBackLater",
                  defaultMessage: "You can try again or check back later.",
                })}
              />
            )}
            {parcelAccountDetails && !isParcelAccountDetailsFetching && (
              <>
                <div className="mb-2xl">
                  <div className="text-sm font-medium text-secondary-v2 mb-sm">
                    {intl.formatMessage({
                      id: "settings.parcelAccounts.editAccount.providerLabel",
                      defaultMessage: "Parcel provider",
                    })}
                  </div>
                  <FedexLogo />
                </div>
                <div className="d-flex align-items-center justify-content-between mb-2xl">
                  <div className="d-flex align-items-center">
                    <div className="text-sm font-medium text-secondary-v2 mr-sm">
                      {intl.formatMessage({
                        id: "settings.parcelAccounts.createAccount.statusLabel",
                        defaultMessage: "Status",
                      })}
                    </div>
                    <Badge
                      label={
                        isActive
                          ? intl.formatMessage({
                              id: "settings.parcelAccounts.createAccount.statusActive",
                              defaultMessage: "Active",
                            })
                          : intl.formatMessage({
                              id: "settings.parcelAccounts.createAccount.statusInactive",
                              defaultMessage: "Inactive",
                            })
                      }
                      color={isActive ? "success" : "gray"}
                      type="status-badge"
                      size="md"
                    />
                  </div>
                  <Controller
                    name="is_active"
                    control={control}
                    render={({ field }) => <Switch id="custom-switch" checked={field.value} onChange={field.onChange} />}
                  />
                </div>
                <div className="edit-parcel-account__configuration-radio">
                  <div className="text-sm font-medium text-secondary-v2 mb-sm">
                    {intl.formatMessage({
                      id: "settings.parcelAccounts.createAccount.configurationLabel",
                      defaultMessage: "Configuration",
                    })}
                  </div>
                  <Controller
                    name="account_type"
                    control={control}
                    render={({ field }) => (
                      <Radio
                        options={[
                          {
                            label: intl.formatMessage({
                              id: "settings.parcelAccounts.createAccount.configurationCompany",
                              defaultMessage: "Company",
                            }),
                            value: "company",
                          },
                          {
                            label: intl.formatMessage({
                              id: "settings.parcelAccounts.createAccount.configurationPaymentLocations",
                              defaultMessage: "Payment locations",
                            }),
                            value: "general",
                          },
                        ]}
                        value={field.value}
                        size="md"
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
                {accountType === "general" && (
                  <div className="mt-sm edit-parcel-account__dual-list">
                    <DualListField
                      required
                      shouldUnregister
                      name="available_to_locations"
                      options={paymentLocations?.map((location: any) => ({
                        label: location.label,
                        value: location.value,
                      }))}
                      optionsListLabel={intl.formatMessage({
                        id: "settings.parcelAccounts.createAccount.availableLocation",
                        defaultMessage: "Available location",
                      })}
                      selectedListLabel={intl.formatMessage({
                        id: "settings.parcelAccounts.createAccount.selectedLocation",
                        defaultMessage: "Selected location",
                      })}
                    />
                  </div>
                )}
                <div className="mt-2xl">
                  <Input
                    label={intl.formatMessage({
                      id: "settings.parcelAccounts.createAccount.accountNameLabel",
                      defaultMessage: "Account name",
                    })}
                    name="account_name"
                    required
                  />
                </div>
                <div className="mt-2xl">
                  <Input
                    label={intl.formatMessage({
                      id: "settings.parcelAccounts.createAccount.accountNumberLabel",
                      defaultMessage: "Account number",
                    })}
                    name="account_number"
                    disabled
                  />
                </div>
                <div className="mt-2xl create-parcel-account__configuration-radio">
                  <div className="text-sm font-medium text-secondary-v2 mb-sm required">
                    {intl.formatMessage({
                      id: "settings.parcelAccounts.createAccount.labelFormatLabel",
                      defaultMessage: "Create labels in",
                    })}
                  </div>
                  <Controller
                    name="label_format"
                    control={control}
                    render={({ field }) => (
                      <Radio
                        options={[
                          {
                            label: intl.formatMessage({
                              id: "settings.parcelAccounts.createAccount.labelFormatPdf",
                              defaultMessage: "PDF",
                            }),
                            value: "pdf",
                          },
                          {
                            label: intl.formatMessage({
                              id: "settings.parcelAccounts.createAccount.labelFormatZpl",
                              defaultMessage: "ZPL",
                            }),
                            value: "zpl",
                          },
                        ]}
                        value={field.value}
                        size="md"
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
                <div className="mt-2xl">
                  <div className="text-sm font-medium text-primary mb-sm">
                    {intl.formatMessage({
                      id: "settings.parcelAccounts.createAccount.labelReferencesLabel",
                      defaultMessage: "Label References",
                    })}
                  </div>
                </div>
                <Row className="mt-xl">
                  <Col md={6}>
                    <Controller
                      name="inv_reference_field"
                      control={control}
                      render={({ field }) => (
                        <div>
                          <label className="cb-select__label">
                            {intl.formatMessage({
                              id: "settings.parcelAccounts.createAccount.referenceLine1Label",
                              defaultMessage: "Reference - INV",
                            })}
                          </label>
                          <Select
                            placeholder={intl.formatMessage({
                              id: "settings.parcelAccounts.createAccount.referenceNotSet",
                              defaultMessage: "Not set yet",
                            })}
                            options={referenceOptions}
                            onChange={(option: any) => field.onChange(option ? option.value : null)}
                            value={referenceOptions.find((o) => o.value === field.value) || null}
                            classNamePrefix="cb-select"
                            isSearchable={false}
                            isClearable
                            menuPortalTarget={document.body}
                            menuPlacement="auto"
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                          />
                        </div>
                      )}
                    />
                  </Col>
                </Row>
                <Row className="mt-xl">
                  <Col md={6}>
                    <Controller
                      name="po_reference_field"
                      control={control}
                      render={({ field }) => (
                        <div>
                          <label className="cb-select__label">
                            {intl.formatMessage({
                              id: "settings.parcelAccounts.createAccount.referenceLine3Label",
                              defaultMessage: "Reference - PO",
                            })}
                          </label>
                          <Select
                            placeholder={intl.formatMessage({
                              id: "settings.parcelAccounts.createAccount.referenceNotSet",
                              defaultMessage: "Not set yet",
                            })}
                            options={referenceOptions}
                            onChange={(option: any) => field.onChange(option ? option.value : null)}
                            value={referenceOptions.find((o) => o.value === field.value) || null}
                            classNamePrefix="cb-select"
                            isSearchable={false}
                            isClearable
                            menuPortalTarget={document.body}
                            menuPlacement="auto"
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                          />
                        </div>
                      )}
                    />
                  </Col>
                </Row>
                <Row className="mt-xl">
                  <Col md={6}>
                    <Controller
                      name="dept_reference_field"
                      control={control}
                      render={({ field }) => (
                        <div>
                          <label className="cb-select__label">
                            {intl.formatMessage({
                              id: "settings.parcelAccounts.createAccount.referenceLine4Label",
                              defaultMessage: "Reference - DEPT",
                            })}
                          </label>
                          <Select
                            placeholder={intl.formatMessage({
                              id: "settings.parcelAccounts.createAccount.referenceNotSet",
                              defaultMessage: "Not set yet",
                            })}
                            options={referenceOptions}
                            onChange={(option: any) => field.onChange(option ? option.value : null)}
                            value={referenceOptions.find((o) => o.value === field.value) || null}
                            classNamePrefix="cb-select"
                            isSearchable={false}
                            isClearable
                            menuPortalTarget={document.body}
                            menuPlacement="auto"
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                          />
                        </div>
                      )}
                    />
                  </Col>
                </Row>
                <Row className="mt-xl">
                  <Col md={6}>
                    <Controller
                      name="ref_reference_field"
                      control={control}
                      render={({ field }) => (
                        <div>
                          <label className="cb-select__label">
                            {intl.formatMessage({
                              id: "settings.parcelAccounts.createAccount.referenceLine2Label",
                              defaultMessage: "Reference - REF",
                            })}
                          </label>
                          <Select
                            placeholder={intl.formatMessage({
                              id: "settings.parcelAccounts.createAccount.referenceNotSet",
                              defaultMessage: "Not set yet",
                            })}
                            options={referenceOptions}
                            onChange={(option: any) => field.onChange(option ? option.value : null)}
                            value={referenceOptions.find((o) => o.value === field.value) || null}
                            classNamePrefix="cb-select"
                            isSearchable={false}
                            isClearable
                            menuPortalTarget={document.body}
                            menuPlacement="auto"
                            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                          />
                        </div>
                      )}
                    />
                  </Col>
                </Row>
                <div className="mt-xl d-flex align-items-center justify-content-end">
                  <Button
                    variant="secondary-gray"
                    label={intl.formatMessage({
                      id: "settings.parcelAccounts.createAccount.discard",
                      defaultMessage: "Discard",
                    })}
                    size="md"
                    type="button"
                    className="mr-md"
                    onClick={handleDiscard}
                  />
                  <Button
                    variant="primary"
                    label={
                      isUpdating
                        ? intl.formatMessage({
                            id: "general.saving",
                            defaultMessage: "Saving...",
                          })
                        : intl.formatMessage({
                            id: "general.save",
                            defaultMessage: "Save",
                          })
                    }
                    size="md"
                    type="submit"
                    disabled={isUpdating || !isValid}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        <UnsavedChangesModal />
      </form>
    </FormProvider>
  );
}

export default EditParcelAccount;
